import { createSlice, PayloadAction } from "@reduxjs/toolkit"

import {
    bookingFlowInitialState,
    type BookingFlowMode,
    type BookingFlowState,
    type BookingFlowCommonState,
    type BookingFlowBookingState,
} from "./types"

import { Pages } from "@modules/BookingCommon/common/constants"
import { CURRENCY } from "@utils/constants"
import type {
    GetBookingDetailsResponse,
    OnlineOrOffline,
    DepositConfiguration,
    SessionTypePartial,
    TimeRequestSelectors,
    DatePickerError,
} from "@modules/BookingCommon/common/interfaces"
import type { PossibleHoursRecord, UtcTime } from "@modules/Booking/common/interfaces"
import type { BookingStatus, OfferedService, Psychologist } from "@utils/interfaces"
import type { ServicesGroup } from "@utils/helpers/ServicesGroup"
import { SESSION_TYPE } from "@modules/BookingCommon/common/constants/sessionType"
import { PaymentMethod } from "@modules/ClientAccount/utils"

const RESTORABLE_COMMON_KEYS: readonly (keyof BookingFlowCommonState)[] = [
    "currency",
    "onlineOrOffline",
    "isOnline",
    "serviceGroups",
    "serviceGroup",
    "selectedService",
    "selectedDiscount",
    "sessionType",
    "deposits",
    "selectedDeposit",
]

const RESTORABLE_BOOKING_KEYS: readonly (keyof BookingFlowBookingState)[] = [
    "psy",
    "hasSlots",
    "slotId",
    "slotDuration",
    "selectedTime",
    "selectedDayOfMonth",
    "currentWeek",
    "email",
    "hasPreviousSessions",
    "hasPreviousSessionsWithSamePsychologist",
    "timeRequestModalActive",
    "isChooseManuallyService",
    "chooseFlowWithNoAvailable",
    "canChangeHistory",
    "requestTimesSelectors",
    "datePickerIsLoaded",
    "datePickerError",
    "possibleHours",
    "timeRequestsUtcTime",
    "hash",
    "trFlowFromPsyProfile",
    "returnFromBooking",
]

function restoreDefinedFields<T extends Record<string, unknown>>(
    target: T,
    source: Partial<T>,
    keys: readonly (keyof T)[]
): void {
    for (const key of keys) {
        if (source[key] !== undefined) {
            ;(target as Record<string, unknown>)[key as string] = source[key]
        }
    }
}

export const bookingFlowSlice = createSlice({
    name: "bookingFlow",
    initialState: bookingFlowInitialState,
    reducers: {
        initFlow(
            state,
            action: PayloadAction<{
                mode: BookingFlowMode
                pages: Pages[]
                initialPage?: Pages
            }>
        ) {
            state.mode = action.payload.mode
            state.pages = action.payload.pages
            state.currentPage = action.payload.initialPage ?? action.payload.pages[0]
            state.errorMessage = undefined
        },

        setPages(state, action: PayloadAction<Pages[]>) {
            state.pages = action.payload
        },

        setCurrentPage(state, action: PayloadAction<Pages>) {
            state.currentPage = action.payload
        },

        nextPage(state) {
            const index = state.pages.findIndex((p) => p === state.currentPage)
            const next = state.pages[index + 1]
            if (next) state.currentPage = next
        },

        setCurrency(state, action: PayloadAction<CURRENCY>) {
            state.currency = action.payload
        },

        setOnlineOrOffline(state, action: PayloadAction<OnlineOrOffline | undefined>) {
            state.onlineOrOffline = action.payload
            state.isOnline = action.payload === "online"
        },

        setServiceGroups(state, action: PayloadAction<ServicesGroup[] | undefined>) {
            state.serviceGroups = action.payload
        },

        setServiceGroup(state, action: PayloadAction<ServicesGroup | undefined>) {
            state.serviceGroup = action.payload
        },

        setSelectedService(state, action: PayloadAction<OfferedService | undefined>) {
            state.selectedService = action.payload
        },

        setSelectedDiscount(state, action: PayloadAction<number | undefined>) {
            state.selectedDiscount = action.payload
        },

        setIsLoading(state, action: PayloadAction<boolean>) {
            state.isLoading = action.payload
        },

        setErrorMessage(state, action: PayloadAction<string | undefined>) {
            state.errorMessage = action.payload
        },

        setBookingStatus(state, action: PayloadAction<BookingStatus | undefined>) {
            state.bookingStatus = action.payload
        },

        /**
         * COMMON: sessionType, deposits (used in both booking and payment)
         */
        setSessionType(state, action: PayloadAction<SessionTypePartial>) {
            const nextType = action.payload ?? SESSION_TYPE.DEFAULT
            state.sessionType = nextType
            state.isPriceLoading = true

            // если TIME_REQUEST → currency = RUB
            if (nextType === SESSION_TYPE.TIME_REQUEST) {
                state.currency = CURRENCY.RUB
            }
        },

        setPriceLoading(state, action: PayloadAction<boolean>) {
            state.isPriceLoading = action.payload
        },

        setDeposits(state, action: PayloadAction<DepositConfiguration[] | undefined>) {
            state.deposits = action.payload
            // обновляем selectedDeposit: если не выбран или текущий не в новом списке — берём первый
            const isCurrentValid =
                !!state.selectedDeposit &&
                action.payload?.some((d) => d.id === state.selectedDeposit?.id)
            if (!isCurrentValid) {
                state.selectedDeposit = action.payload?.[0]
            }
        },

        setSelectedDeposit(state, action: PayloadAction<DepositConfiguration | undefined>) {
            state.selectedDeposit = action.payload
        },

        /**
         * PAYMENT MODE
         */
        setPaymentBookingDetails(
            state,
            action: PayloadAction<GetBookingDetailsResponse | undefined>
        ) {
            state.payment.bookingDetails = action.payload
        },

        /**
         * BOOKING MODE
         */
        setBookingPsychologist(state, action: PayloadAction<Psychologist | undefined>) {
            state.booking.psy = action.payload
        },

        setBookingHasSlots(state, action: PayloadAction<boolean>) {
            state.booking.hasSlots = action.payload
        },

        setBookingSlotId(state, action: PayloadAction<string | undefined>) {
            state.booking.slotId = action.payload
        },

        setBookingSlotDuration(state, action: PayloadAction<number | undefined>) {
            state.booking.slotDuration = action.payload
        },

        setBookingSelectedTime(state, action: PayloadAction<Date | undefined>) {
            state.booking.selectedTime = action.payload
        },

        setBookingSelectedDayOfMonth(state, action: PayloadAction<Date | undefined>) {
            state.booking.selectedDayOfMonth = action.payload
        },

        setBookingCurrentWeek(state, action: PayloadAction<Date | undefined>) {
            state.booking.currentWeek = action.payload
        },

        setBookingRequestTimesSelectors(state, action: PayloadAction<TimeRequestSelectors[]>) {
            state.booking.requestTimesSelectors = action.payload
        },

        setBookingDatePickerIsLoaded(state, action: PayloadAction<boolean>) {
            state.booking.datePickerIsLoaded = action.payload
        },

        setBookingDatePickerError(state, action: PayloadAction<DatePickerError | undefined>) {
            state.booking.datePickerError = action.payload
        },

        setBookingPossibleHours(state, action: PayloadAction<PossibleHoursRecord | null>) {
            state.booking.possibleHours = action.payload
        },

        setBookingTimeRequestsUtcTime(state, action: PayloadAction<UtcTime | null>) {
            state.booking.timeRequestsUtcTime = action.payload
        },

        setBookingHash(state, action: PayloadAction<string | undefined>) {
            state.booking.hash = action.payload
        },

        setBookingEmail(state, action: PayloadAction<string | undefined>) {
            state.booking.email = action.payload
        },

        setBookingHasPreviousSessions(state, action: PayloadAction<boolean>) {
            state.booking.hasPreviousSessions = action.payload
        },

        setBookingHasPreviousSessionsWithSamePsychologist(state, action: PayloadAction<boolean>) {
            state.booking.hasPreviousSessionsWithSamePsychologist = action.payload
        },

        setBookingTimeRequestModalActive(state, action: PayloadAction<boolean>) {
            state.booking.timeRequestModalActive = action.payload
        },

        setBookingIsChooseManuallyService(state, action: PayloadAction<boolean>) {
            state.booking.isChooseManuallyService = action.payload
        },

        setBookingChooseFlowWithNoAvailable(state, action: PayloadAction<boolean>) {
            state.booking.chooseFlowWithNoAvailable = action.payload
        },

        setBookingCanChangeHistory(state, action: PayloadAction<boolean>) {
            state.booking.canChangeHistory = action.payload
        },

        setBookingTrFlowFromPsyProfile(state, action: PayloadAction<boolean>) {
            state.booking.trFlowFromPsyProfile = action.payload
        },

        setBookingReturnFromBooking(state, action: PayloadAction<boolean>) {
            state.booking.returnFromBooking = action.payload
        },

        setBookingWasStateRestored(state, action: PayloadAction<boolean>) {
            state.booking.wasStateRestored = action.payload
        },

        restoreBookingState(state, action: PayloadAction<Partial<BookingFlowState>>) {
            const saved = action.payload
            // Восстанавливаем common state
            restoreDefinedFields(state, saved, RESTORABLE_COMMON_KEYS)

            // isOnline — производное от onlineOrOffline, но не сохраняется в storage отдельно.
            // Пересчитываем после восстановления, чтобы не показывать "Лично" при onlineOrOffline === "online".
            if (saved.onlineOrOffline !== undefined) {
                state.isOnline = saved.onlineOrOffline === "online"
            }

            // Восстанавливаем booking state
            if (saved.booking) {
                restoreDefinedFields(state.booking, saved.booking, RESTORABLE_BOOKING_KEYS)
            }
        },

        resetFlow(state) {
            Object.assign(state, bookingFlowInitialState)
        },

        setPaymentPayByCard(state, action: PayloadAction<PaymentMethod | null>) {
            state.payment.payByCard = action.payload
        },

        setPaymentBookSessionIsLoading(state, action: PayloadAction<boolean>) {
            state.payment.bookSessionIsLoading = action.payload
        },
    },
})

export const bookingFlowActions = bookingFlowSlice.actions
export const bookingFlowReducer = bookingFlowSlice.reducer
